import * as React from 'react';

import { useQuery } from '@apollo/client';

import requestUrlInfo from 'js/lib/requestUrlInfo';

import ValuePropositionContext from 'bundles/browse-common/contexts/ValuePropositionContext';
import { useSearchFacetCounts } from 'bundles/browse-common/hooks/useSearchFacetCounts';
import BrowseSeoPathConfiguration from 'bundles/browse-common/queries/BrowseSeoPathConfiguration.graphql';
import { EnterpriseSkillDetails } from 'bundles/browse-common/queries/EnterpriseSkillDetails';
import type {
  BrowseSeoPathConfigurationQuery,
  BrowseSeoPathConfigurationQueryVariables,
} from 'bundles/browse-common/queries/__generated__/BrowseSeoPathConfiguration';
import { type Skill } from 'bundles/browse-common/types/Skill';
import type { ValuePropositionItem } from 'bundles/browse-common/types/valueProposition';
import { getPathWithoutRenderModeAndMockDev } from 'bundles/seo/api/SeoPathConfigurationQueryWrapper';

import _t from 'i18n!nls/browse-common';

type Props = {
  skill: Skill;
  children: React.ReactNode;
};

type ValuePropositionProductType = 'credentials' | 'degrees' | 'courses';

const useBrowseSeoPathConfiguration = (skillId: string) => {
  const hostname = requestUrlInfo().hostname;
  const processedPath = getPathWithoutRenderModeAndMockDev(hostname);

  const { data, loading } = useQuery<BrowseSeoPathConfigurationQuery, BrowseSeoPathConfigurationQueryVariables>(
    BrowseSeoPathConfiguration,
    {
      variables: { path: processedPath + '/courses?query=' + skillId },
      context: { clientName: 'gatewayGql' },
      errorPolicy: 'all',
    }
  );

  return { data, loading };
};

const useEnterpriseSkillDetails = (skillId: string, shouldSkip: boolean) => {
  type Variables = { input: { skillId: string } };
  type Response = { GetSkill: { skill?: { skillDescription?: string | null } | null } };

  const { data, loading } = useQuery<Response, Variables>(EnterpriseSkillDetails, {
    variables: { input: { skillId } },
    skip: shouldSkip,
  });

  return { data, loading };
};

const getPluralizedLabel = (valuePropProductType: ValuePropositionProductType, count: number) => {
  switch (valuePropProductType) {
    case 'credentials':
      return count === 1 ? _t('1 credential') : _t('#{count} credentials', { count });
    case 'degrees':
      return count === 1 ? _t('1 online degree') : _t('#{count} online degrees', { count });
    case 'courses':
      return count === 1 ? _t('1 course') : _t('#{count} courses', { count });
    default:
      return '';
  }
};

const getValuePropositionItems = ({
  credentials,
  degrees,
  courses,
}: {
  credentials: number;
  degrees: number;
  courses: number;
}) => {
  const items: ValuePropositionItem[] = [];
  if (credentials > 0) {
    items.push({ text: getPluralizedLabel('credentials', credentials) });
  }
  if (degrees > 0) {
    items.push({ text: getPluralizedLabel('degrees', degrees) });
  }
  if (courses > 0) {
    items.push({ text: getPluralizedLabel('courses', courses) });
  }
  return items;
};

const SkillValuePropProvider = ({ skill, children }: Props) => {
  // Primary source for description is from the EQP data
  const { data, loading: isLoadingSeoPathConfiguration } = useBrowseSeoPathConfiguration(skill.id);
  const eqpDescription = data?.Seo?.seoPathConfigurationQuery?.eqpData?.topContentBlock?.content;
  // Fallback source for description is from the enterprise skill details
  const { data: enterpriseData, loading: isLoadingEnterpriseSkillDetails } = useEnterpriseSkillDetails(
    skill.id,
    Boolean(eqpDescription)
  );
  const enterpriseSkillDescription = enterpriseData?.GetSkill?.skill?.skillDescription;

  // TODO: Refactor to use useFacetCounts from search-common once it supports the SearchFrame/Browse pages
  const {
    credentialsCount,
    degreesCount,
    coursesCount,
    isLoading: isLoadingSearch,
  } = useSearchFacetCounts('productTypeDescription', {
    credentialsCount: ['Professional Certificates', 'Specializations'],
    degreesCount: 'Degrees',
    coursesCount: 'Courses',
  });

  const valuePropositionItems = getValuePropositionItems({
    credentials: credentialsCount,
    degrees: degreesCount,
    courses: coursesCount,
  });

  return (
    <ValuePropositionContext.Provider
      value={{
        title: skill.skillName,
        description: eqpDescription || enterpriseSkillDescription,
        valuePropositionItems,
        loading: isLoadingSeoPathConfiguration || isLoadingEnterpriseSkillDetails || isLoadingSearch,
      }}
    >
      {children}
    </ValuePropositionContext.Provider>
  );
};

export default SkillValuePropProvider;
